if (!customElements.get('mb-recommended-products')) {
  customElements.define(
    'mb-recommended-products',
    class extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.items = Array.from(
          this.querySelectorAll('[data-recommended-item]')
        );
        this.buttons = Array.from(
          this.querySelectorAll('[data-recommended-add]')
        );

        this.moreButton = this.querySelector('[data-recommended-more]');
        this.errorElement = this.querySelector('[data-recommended-error]');
        this.statusElement = this.querySelector('[data-recommended-status]');

        this.expanded = false;
        this.pendingAdds = 0;
        this.syncSequence = (this.syncSequence || 0) + 1;
        this.connectionId = (this.connectionId || 0) + 1;

        this.buttons.forEach((button) => {
          button.dataset.originalAriaLabel =
            button.dataset.originalAriaLabel ||
            button.getAttribute('aria-label') ||
            '';

          this.setButtonState(
            button,
            button.closest('[data-recommended-item]')
              ?.classList.contains('is-added') || false
          );
        });

        this.onAdd = (event) => {
          const button = event.target.closest('[data-recommended-add]');

          if (!button || !this.contains(button)) return;

          this.addToCart(button);
        };

        this.onToggle = () => {
          this.expanded = !this.expanded;
          this.updateVisibility();
        };

        this.onCartChanged = () => {
          // Il payload dell'evento può contenere solo il prodotto aggiunto.
          // Rileggiamo sempre il carrello completo.
          this.scheduleSync();
        };

        this.onReturn = () => {
          if (!document.hidden) {
            this.scheduleSync(0);
          }
        };

        this.addEventListener('click', this.onAdd);
        this.moreButton?.addEventListener('click', this.onToggle);

        document.addEventListener('mb:cart:changed', this.onCartChanged);
        document.addEventListener('visibilitychange', this.onReturn);
        window.addEventListener('focus', this.onReturn);
        window.addEventListener('pageshow', this.onReturn);

        // Controllo di riserva per rimozioni che non emettono
        // l'evento mb:cart:changed.
        this.pollTimer = window.setInterval(() => {
          if (!document.hidden && !this.syncController) {
            this.scheduleSync(0);
          }
        }, 5000);

        this.updateVisibility();
        this.scheduleSync(0);
      }

      disconnectedCallback() {
        this.initialized = false;
        this.connectionId = (this.connectionId || 0) + 1;
        this.syncSequence = (this.syncSequence || 0) + 1;

        this.removeEventListener('click', this.onAdd);
        this.moreButton?.removeEventListener('click', this.onToggle);

        document.removeEventListener('mb:cart:changed', this.onCartChanged);
        document.removeEventListener('visibilitychange', this.onReturn);
        window.removeEventListener('focus', this.onReturn);
        window.removeEventListener('pageshow', this.onReturn);

        window.clearTimeout(this.syncTimer);
        window.clearInterval(this.pollTimer);

        this.syncController?.abort();
        this.syncController = null;
      }

      updateVisibility() {
        const visibleCount = Math.max(
          1,
          Math.floor(Number(this.dataset.visibleCount) || 2)
        );

        this.items.forEach((item, index) => {
          item.hidden = !this.expanded && index >= visibleCount;
        });

        if (!this.moreButton) return;

        this.moreButton.hidden = this.items.length <= visibleCount;
        this.moreButton.setAttribute(
          'aria-expanded',
          String(this.expanded)
        );

        const label = this.moreButton.querySelector('[data-more-label]');

        if (label) {
          label.textContent = this.expanded
            ? this.moreButton.dataset.showLess
            : this.moreButton.dataset.showMore;
        }
      }

      getCartUrl() {
        const root = window.Shopify?.routes?.root || '/';

        return this.dataset.cartUrl || `${root}cart.js`;
      }

      getAddUrl() {
        const root = window.Shopify?.routes?.root || '/';
        const url = this.dataset.cartAddUrl || `${root}cart/add.js`;

        // Compatibile anche con il precedente attributo senza ".js".
        return url.endsWith('.js') ? url : `${url}.js`;
      }

      setButtonState(button, inCart) {
        const item = button.closest('[data-recommended-item]');
        const label = button.querySelector('[data-recommended-add-label]');
        const busy = button.getAttribute('aria-busy') === 'true';

        item?.classList.toggle('is-added', inCart);
        button.classList.toggle('is-success', inCart);
        button.dataset.inCart = String(inCart);

        if (label) {
          label.textContent = inCart
            ? button.dataset.labelAdded
            : button.dataset.labelAdd;
        }

        // Rimane raggiungibile da tastiera anche quando già aggiunto.
        button.setAttribute('aria-disabled', String(inCart || busy));

        if (inCart) {
          button.setAttribute(
            'aria-label',
            `${button.dataset.labelAdded}: ${button.dataset.productName}`
          );
        } else {
          button.setAttribute(
            'aria-label',
            button.dataset.originalAriaLabel
          );
        }
      }

      applyCart(cart) {
        if (!cart || !Array.isArray(cart.items)) return;

        const variantIds = new Set(
          cart.items
            .filter((item) => Number(item.quantity) > 0)
            .map((item) => String(item.variant_id ?? item.id))
        );

        this.buttons.forEach((button) => {
          this.setButtonState(
            button,
            variantIds.has(String(button.dataset.variantId))
          );
        });
      }

      scheduleSync(delay = 120) {
        window.clearTimeout(this.syncTimer);

        if (!this.isConnected || document.hidden) return;

        this.syncTimer = window.setTimeout(() => {
          this.syncCart();
        }, delay);
      }

      async syncCart() {
        if (
          !this.isConnected ||
          document.hidden ||
          this.pendingAdds > 0
        ) {
          return;
        }

        this.syncController?.abort();

        const controller = new AbortController();
        const sequence = ++this.syncSequence;

        this.syncController = controller;

        try {
          const response = await fetch(this.getCartUrl(), {
            method: 'GET',
            credentials: 'same-origin',
            cache: 'no-store',
            headers: {
              Accept: 'application/json',
            },
            signal: controller.signal,
          });

          if (!response.ok) return;

          const cart = await response.json();

          if (
            !this.isConnected ||
            sequence !== this.syncSequence ||
            this.pendingAdds > 0
          ) {
            return;
          }

          this.applyCart(cart);
        } catch (error) {
          // Se la lettura fallisce, conserviamo l'ultimo stato noto.
          // Il prossimo evento o controllo periodico riproverà.
        } finally {
          if (this.syncController === controller) {
            this.syncController = null;
          }
        }
      }

      async addToCart(button) {
        if (
          !button ||
          button.disabled ||
          button.dataset.inCart === 'true'
        ) {
          return;
        }

        const connectionId = this.connectionId;
        let addedData = null;

        this.pendingAdds += 1;

        // Invalida eventuali letture iniziate prima dell'aggiunta.
        this.syncSequence += 1;
        this.syncController?.abort();
        this.syncController = null;

        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-disabled', 'true');

        if (this.errorElement) {
          this.errorElement.hidden = true;
          this.errorElement.textContent = '';
        }

        if (this.statusElement) {
          this.statusElement.textContent = '';
        }

        try {
          const body = new FormData();

          body.append('id', button.dataset.variantId);
          body.append('quantity', '1');
          body.append('sections', 'mb-cart-drawer,mb-cart,header');
          body.append(
            'sections_url',
            window.location.pathname + window.location.search
          );

          const response = await fetch(this.getAddUrl(), {
            method: 'POST',
            credentials: 'same-origin',
            body,
            headers: {
              Accept: 'application/json',
            },
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(
              data.description ||
              data.message ||
              this.dataset.errorMessage
            );
          }

          addedData = data;

          if (
            this.isConnected &&
            this.connectionId === connectionId
          ) {
            this.setButtonState(button, true);

            if (this.statusElement) {
              this.statusElement.textContent =
                this.dataset.addedMessage || button.dataset.labelAdded;
            }
          }
        } catch (failure) {
          if (
            this.isConnected &&
            this.connectionId === connectionId &&
            this.errorElement
          ) {
            this.errorElement.textContent =
              failure.message || this.dataset.errorMessage;
            this.errorElement.hidden = false;
          }
        } finally {
          button.disabled = false;
          button.setAttribute('aria-busy', 'false');

          if (this.connectionId === connectionId) {
            this.pendingAdds = Math.max(0, this.pendingAdds - 1);

            this.setButtonState(
              button,
              button.dataset.inCart === 'true'
            );

            this.scheduleSync(0);
          }
        }

        if (!addedData) return;

        // Mantiene l'integrazione con il carrello del tema.
        document.dispatchEvent(
          new CustomEvent('mb:cart:changed', {
            detail: { cart: addedData },
          })
        );

        try {
          if (typeof window.mbCart?.open === 'function') {
            window.mbCart.open();
          } else {
            document
              .querySelector('[data-mb-cart-drawer]')
              ?.setAttribute('aria-open', 'true');
          }
        } catch (error) {
          // Il prodotto è già stato aggiunto correttamente.
          // Un problema di apertura del drawer non cambia questo stato.
          console.warn(
            'Impossibile aprire il carrello dopo l’aggiunta.',
            error
          );
        }
      }
    }
  );
}